import React from 'react';
import { FileUpload, SpaceBetween } from '@amzn/awsui-components-react';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Link from '@amzn/awsui-components-react/polaris/link';
import { externalLinks } from '../data/externalLinks';
import Button from '@amzn/awsui-components-react/polaris/button';
import { useTranslation } from 'react-i18next';
import { createBulkUpdate } from '../api/bulk';
import { useNavigate } from 'react-router-dom';
import { LocalizedTrans } from '../hooks/localization/localizedTrans';
import { useLocalizedText } from '../hooks/localizedText';

export function BulkUpdateForm({ hasPermission }: { hasPermission: boolean }) {
  const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(null);
  const navigate = useNavigate();
  const [preProcessing, setPreProcessing] = React.useState(false);
  const { t } = useLocalizedText(['translation']);
  // The instructions sentence is three fragment keys in the i18next source, and one tagged message in
  // RedVelvetTranslations under the id of the middle fragment — so bulkUpdate.uploadInstructions means
  // the link's label here and the whole sentence there. Read the fragments from i18next directly, or
  // the label comes back as the sentence and renders its own markup as the link text. The pack
  // contract test holds the other half of this: that only one bundle carries the id.
  const { t: fragmentText } = useTranslation();

  const validFilesSelected =
    selectedFiles !== null && !selectedFiles.find((file) => !file.name.endsWith('.csv'));

  const instructionsLink = (
    <Link
      href={externalLinks.bulkUpload.bulkUpdateInstructions}
      external
      variant="primary"
      fontSize="body-s"
      data-aci-analytics-name="bulkUpdateInstructions"
    />
  );

  return (
    <SpaceBetween direction="vertical" size="m">
      <FormField
        label={t('bulkUpdate.bulkUpdateFile')}
        description={
          <LocalizedTrans
            i18nKey="bulkUpdate.uploadInstructions"
            components={{ link: instructionsLink }}
            fallback={() => (
              <>
                {fragmentText('bulkUpdate.beforeUploadInstructions')}
                {React.cloneElement(
                  instructionsLink,
                  {},
                  fragmentText('bulkUpdate.uploadInstructions')
                )}
                {fragmentText('bulkUpdate.afterUploadInstructions')}
              </>
            )}
          />
        }
      >
        <FileUpload
          onChange={({ detail }) => setSelectedFiles(detail.value)}
          value={selectedFiles || []}
          accept=".csv"
          showFileLastModified
          showFileSize
          showFileThumbnail
          i18nStrings={{
            uploadButtonText: (e) => t('bulkUpdate.chooseFile'),
            dropzoneText: (e) => t('bulkUpdate.dropFile'),
            removeFileAriaLabel: (e) => t('bulkUpdate.removeFile'),
            limitShowFewer: t('bulkUpdate.showFewer'),
            limitShowMore: t('bulkUpdate.showMore'),
            errorIconAriaLabel: t('bulkUpdate.error'),
          }}
        />
      </FormField>
      <Button
        variant="primary"
        disabled={!validFilesSelected}
        loading={preProcessing}
        onClick={() => {
          (async () => {
            if (selectedFiles && selectedFiles.length > 0) {
              setPreProcessing(true);
              const text = await selectedFiles[0].text();
              const result = await createBulkUpdate(text);
              const filename = result['fileName'];
              navigate(`/bulk-update?filename=${filename}`);
            }
            setPreProcessing(false);
          })();
        }}
      >
        {preProcessing ? t('bulkUpdate.verifying') : t('bulkUpdate.submit')}
      </Button>
    </SpaceBetween>
  );
}
